import React, { type FC, useEffect } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import { notifyParent } from 'mailup-react-helpers'
import {
  DataListHeader,
  DataListRow,
  Flex,
  GRID_VARIANT_SMALL,
  LINK_VARIANT_DEFAULT,
  Link,
  PaginationBar,
  Panel,
  SPACING_16,
  SpacingVertical,
  TITLE_VARIANT_M,
  Title,
  TooltipUtils,
} from 'mailup-web-components'

import IconPlus from 'Components/icons/IconPlus'

import ResultItem from 'Pages/UnsubscriptionCampaignsList/components/ResultItem'
import ResultSkel from 'Pages/UnsubscriptionCampaignsList/components/ResultSkel'

import RenderWhen from 'Components/RenderWhen'
import {
  modalCopyOpenCallback,
  modalDeleteOpenCallback,
  modalSimulateOpenCallback,
} from 'Pages/UnsubscriptionCampaignsList/recoil/callbacks/modals'
import {
  fetchResultsCallback,
  toggleStatusCallback,
} from 'Pages/UnsubscriptionCampaignsList/recoil/callbacks/results'
import {
  paginationState,
  resultsState,
  utilsState,
} from 'Pages/UnsubscriptionCampaignsList/recoil/state/results'

const BoxResults: FC = () => {
  const intl = useIntl()

  const {
    orderBy,
    orderDescending,
    pageNumber,
    pageSize,
    totalItems,
    loading,
  } = useRecoilValue(paginationState)
  const items = useRecoilValue(resultsState)
  const { canCreate } = useRecoilValue(utilsState)

  const fetchResults = useRecoilCallback(fetchResultsCallback, [])

  const copy = useRecoilCallback(modalCopyOpenCallback, [])
  const simulate = useRecoilCallback(modalSimulateOpenCallback, [])
  const deleteWf = useRecoilCallback(modalDeleteOpenCallback, [])
  const toggle = useRecoilCallback(toggleStatusCallback, [])

  useEffect(() => {
    setTimeout(() => {
      TooltipUtils.rebuild()
    }, 0)
  }, [canCreate, items])

  const handleNewWorkflow = (): void => {
    const route = '/automation/workflow/0/edit?wt=UnsubscriptionCampaign'
    notifyParent('redirectMainFrame', [
      `automation_app_redirector.aspx?r=${encodeURIComponent(route)}`,
    ])
  }

  const handleSort = (pOrderBy: string): void => {
    void fetchResults({ orderBy: pOrderBy })
  }

  const handleEdit = (idWorkflow: number) => (): void => {
    const route = `/automation/workflow/${idWorkflow}/edit`
    notifyParent('redirectMainFrame', [
      `automation_app_redirector.aspx?r=${encodeURIComponent(route)}`,
    ])
  }

  const handleToggleEnabled = (idWorkflow: number) => (): void => {
    void toggle(idWorkflow)
  }

  const handleCopy = (idWorkflow: number) => (): void => {
    void copy(idWorkflow)
  }

  const handleSimulate = (idWorkflow: number) => (): void => {
    void simulate(idWorkflow)
  }

  const handleStats = (idWorkflow: number) => (): void => {
    const route = `/automation/workflows/statistics/${idWorkflow}`
    notifyParent('redirectMainFrame', [
      `automation_app_redirector.aspx?r=${encodeURIComponent(route)}`,
    ])
  }

  const handleDelete = (idWorkflow: number, name: string) => (): void => {
    void deleteWf(idWorkflow, name)
  }

  const handleChangePage = (pageNumber: number): void => {
    void fetchResults({
      pageNumber,
    })
  }

  const handleChangePageSize = (pageSize: number): void => {
    void fetchResults({
      pageNumber: 1,
      pageSize,
    })
  }

  const heads = [
    {
      field: 'Name',
      label: intl.formatMessage({
        id: 'Load.UnsubscriptionCampaignsList.ColumnName',
      }),
      span: 3,
      sortable: true,
    },
    {
      field: 'Type',
      label: intl.formatMessage({
        id: 'Load.UnsubscriptionCampaignsList.ColumnType',
      }),
      span: 3,
      sortable: false,
    },
    {
      field: 'LastModifiedDate',
      label: intl.formatMessage({
        id: 'Load.AutomationWorkflow.LastUpdate',
      }),
      span: 2,
      sortable: true,
    },
    {
      field: 'LastActivationDate',
      label: intl.formatMessage({
        id: 'Load.AutomationWorkflow.LastActivation',
      }),
      span: 2,
      sortable: true,
    },
    {
      field: 'Status',
      label: intl.formatMessage({
        id: 'Load.AutomationWorkflow.ListTableStatus',
      }),
      span: 2,
      sortable: false,
    },
  ]

  return (
    <Panel data-mu-test="results" margin="0 0 8px 0">
      <Title variant={TITLE_VARIANT_M}>
        {intl.formatMessage({
          id: 'Load.UnsubscriptionCampaignsList.WorkflowsList',
        })}
      </Title>
      <SpacingVertical variant={SPACING_16} />
      <Flex flexDirection="column" gap="16px">
        <RenderWhen condition={canCreate}>
          <Flex alignItems="center" justifyContent="flex-end">
            <span
              data-tip={
                canCreate
                  ? undefined
                  : intl.formatMessage({
                      id: 'Load.Common.UnavailableService',
                    })
              }
            >
              <Link
                disabled={!canCreate}
                variant={LINK_VARIANT_DEFAULT}
                alignContent
                gap="8px"
                data-mu-test="new-workflow"
                onClick={handleNewWorkflow}
              >
                <IconPlus width="15px" height="16px" />
                {intl.formatMessage({
                  id: 'Load.UnsubscriptionCampaignsList.NewWorkflow',
                })}
              </Link>
            </span>
          </Flex>
        </RenderWhen>

        <section>
          <DataListHeader
            heads={heads}
            orderByStatus={orderBy}
            orderDescendingStatus={orderDescending}
            handleChangeColumnsOrderCallback={handleSort}
            gridVariant={GRID_VARIANT_SMALL}
          />
          <div>
            {items.map((item, key) => (
              <DataListRow key={item.Id}>
                {loading ? (
                  <ResultSkel />
                ) : (
                  <ResultItem
                    canCreate={canCreate}
                    item={item}
                    onEdit={handleEdit}
                    onToggleEnabled={handleToggleEnabled}
                    onCopy={handleCopy}
                    onSimulate={handleSimulate}
                    onStats={handleStats}
                    onDelete={handleDelete}
                  />
                )}
              </DataListRow>
            ))}
          </div>
        </section>

        <PaginationBar
          margin="0"
          minPageSize={5}
          pageNumber={pageNumber}
          pageSize={pageSize}
          totalItems={totalItems}
          handleChangePage={handleChangePage}
          handleChangePageSize={handleChangePageSize}
          strings={{
            of: intl.formatMessage({ id: 'Load.Pager.Of' }),
            label: intl.formatMessage({ id: 'Load.Pager.Items' }),
          }}
          data-mu-test="results-list__pagination"
          formatValues={intl.formatNumber}
        />
      </Flex>
    </Panel>
  )
}

export default BoxResults
